import { useCallback, useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy, where, limit as fbLimit, startAfter } from "firebase/firestore";

/**
 * Datos de prospección compartidos por la lista, el mapa y el inicio del
 * comercial: puntos (prospeccion_puntos), CRM (prospectos_crm) y la lista de
 * usuarios comerciales (para asignar). Se cachean a nivel módulo para no volver
 * a leer Firestore al cambiar de pestaña. `reload()` fuerza refresco; `updateCrm`
 * refleja un guardado del CRM sin recargar todo.
 */
let _cache = null;

// Los puntos se cargan PAGINADOS (4k por request): la colección tiene ~11.7k
// docs (MVD completo + Canelones) y una sola query gigante falla en el SDK web;
// el límite fijo anterior de 5k dejaba invisible a más de la mitad del mapa.
async function fetchPuntos() {
  const PAGE = 4000;
  const MAX = 30000; // tope de cordura; si se supera, pasar a chunks como las parcelas
  const out = [];
  let last = null;
  while (out.length < MAX) {
    const parts = [collection(db, "prospeccion_puntos"), orderBy("score", "desc")];
    if (last) parts.push(startAfter(last));
    parts.push(fbLimit(PAGE));
    const snap = await getDocs(query(...parts));
    out.push(...snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    if (snap.docs.length < PAGE) break;
    last = snap.docs[snap.docs.length - 1];
  }
  return out;
}

async function fetchAll() {
  const [puntosBase, cSnap, comSnap] = await Promise.all([
    fetchPuntos(),
    getDocs(collection(db, "prospectos_crm")),
    getDocs(query(collection(db, "users"), where("typeuser", "==", 4))),
  ]);
  const crmByPadron = {};
  cSnap.forEach((d) => { crmByPadron[d.id] = { id: d.id, ...d.data() }; });

  // Edificios MANUALES (alta desde el buscador del mapa): viven en prospectos_crm
  // con lat/lng propios. No están en prospeccion_puntos (colección read-only), así
  // que los sintetizamos como puntos para que el mapa y la lista los muestren.
  const manuales = Object.values(crmByPadron)
    .filter((c) => c.manual && c.lat != null && c.lng != null)
    .map((c) => ({
      id: String(c.padron ?? c.id), padron: c.padron ?? c.id,
      lat: c.lat, lng: c.lng, score: c.score ?? 50,
      estadoPH: c.estadoPH || "comun", barrio: c.barrio || null,
      direccion: c.direccion || null, nUnidades: c.nUnidades ?? null,
      valorCatastral: null, manual: true, yaAdministrado: false,
    }));
  const puntos = [...manuales, ...puntosBase];

  const comerciales = comSnap.docs.map((d) => {
    const x = d.data();
    const name = x.name || `${x.NombreCompleto || ""} ${x.ApellidosCompleto || ""}`.trim();
    return { userid: x.userid, email: String(x.email || "").toLowerCase(), name: name || x.email };
  });
  return { puntos, crmByPadron, comerciales };
}

export function useProspeccionData() {
  const [state, setState] = useState(() =>
    _cache ? { ..._cache, loading: false, error: null }
           : { puntos: [], crmByPadron: {}, comerciales: [], loading: true, error: null }
  );

  const load = useCallback(async (force = false) => {
    if (_cache && !force) { setState({ ..._cache, loading: false, error: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      _cache = await fetchAll();
      setState({ ..._cache, loading: false, error: null });
    } catch (e) {
      // Visible en consola: antes fallaba mudo y el mapa quedaba en 0 sin pista.
      console.error("[useProspeccionData] carga falló:", e);
      setState((s) => ({ ...s, loading: false, error: e }));
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const updateCrm = useCallback((padron, crm) => {
    const key = String(padron);
    const base = _cache || { puntos: [], crmByPadron: {}, comerciales: [] };
    const merged = { ...(base.crmByPadron[key] || {}), ...crm };
    let puntos = base.puntos;
    // Alta/edición de un edificio manual → reflejar el punto en el mapa al instante.
    if (merged.manual && merged.lat != null && merged.lng != null) {
      const pt = {
        id: key, padron: merged.padron ?? key, lat: merged.lat, lng: merged.lng,
        score: merged.score ?? 50, estadoPH: merged.estadoPH || "comun",
        barrio: merged.barrio || null, direccion: merged.direccion || null,
        nUnidades: merged.nUnidades ?? null, valorCatastral: null, manual: true, yaAdministrado: false,
      };
      const idx = base.puntos.findIndex((p) => String(p.padron) === key);
      puntos = idx >= 0
        ? base.puntos.map((p, i) => (i === idx ? pt : p))
        : [pt, ...base.puntos];
    }
    _cache = { ...base, puntos, crmByPadron: { ...base.crmByPadron, [key]: merged } };
    setState({ ..._cache, loading: false, error: null });
  }, []);

  return { ...state, reload: () => load(true), updateCrm };
}
